import React, { memo, useMemo, useState } from 'react'
import { useDispatch, useSelector } from '@tabeeb/store'

import { UserRole } from '@tabeeb/enums'
import { ConfirmationDialog } from '@collaborate-health/uikit'
import { makePresenter } from '@tabeeb/shared/content/actions'

import UserCertificatesDialog from '@tabeeb/modules/certificates/components/UserCertificatesDialog'
import { useDialogState } from '@tabeeb/modules/shared/utils/hooks'
import HealthInfoDialog from '@tabeeb/modules/healthData/components/HealthInfoDialog'

import { isCurrentUserRecorder } from '@tabeeb/modules/recording/services/localRecordingService'
import { openMakePresenterDialog } from '@tabeeb/modules/whiteboard/actions'
import { getIsSessionGuestRoleEnabled, getSessionRoleDisplayName } from '@tabeeb/modules/appConfigState/selectors'
import { EditContentUserQualificationsDialog } from '@tabeeb/modules/invites/components/EditContentUserQualificationsDialog'
import { getContentId } from '@tabeeb/shared/content/selectors'
import { ContentUserRole } from '@tabeeb/contracts/main-api/swagger-main-api'
import { Popover } from '@mui/material'
import UserOptionButton from '../UserOptionButton'

import {
  getIsMakePresenterButtonVisible,
  getIsShowCertificatesButtonVisible,
  getIsShowHealthDataButtonVisible,
  getIsSwitchRoleButtonsVisible,
  getShowContentUserQualificationsManagementButton,
  makeGetIsKickUserButtonVisible,
} from '../../selectors'

import { kickUser, switchContentUserRole } from '../../actions'
import { PluginUser } from '../../types'

interface UserOptionsMenuProps {
  onClose: () => void
  isOpen: boolean
  anchorEl?: HTMLElement | null
  user: PluginUser
}

const UserOptionsMenu: React.FC<UserOptionsMenuProps> = ({ anchorEl, isOpen, onClose, user }) => {
  const dispatch = useDispatch()
  const [isKickUserDialogOpened, setIsKickUserDialogOpened] = useState(false)
  const [isShowHealthInfoPopupOpened, setIsShowHealthInfoPopupOpened] = useState(false)
  const [certificatesDialogOpen, onCertificatesDialogOpen, onCertificatesDialogClose] = useDialogState()
  const [
    contentUserQualificationsDialogOpen,
    onContentUserQualificationsDialogOpen,
    onContentUserQualificationsDialogClose,
  ] = useDialogState()

  const kickButtonVisibilitySelector = useMemo(makeGetIsKickUserButtonVisible, [])
  const isKickButtonVisible = useSelector((state) => kickButtonVisibilitySelector(state, { user }))
  const isSwitchRoleButtonsVisible = useSelector((state) => getIsSwitchRoleButtonsVisible(state, { user }))
  const isMakePresenterVisible = useSelector((state) => getIsMakePresenterButtonVisible(state, { user }))
  const isShowHealthDataVisible = useSelector((state) => getIsShowHealthDataButtonVisible(state, { user }))
  const isCertificatesMenuVisible = useSelector((state) => getIsShowCertificatesButtonVisible(state, { user }))
  const isContentUserQualificationManagementVisible = useSelector((state) =>
    getShowContentUserQualificationsManagementButton(state, { user })
  )
  const contentId = useSelector(getContentId)

  const onKickUserRequested = () => {
    setIsKickUserDialogOpened(true)
  }

  const onKickUserAborted = () => {
    setIsKickUserDialogOpened(false)
  }

  const onKickUserSubmitted = () => {
    setIsKickUserDialogOpened(false)
    dispatch(kickUser(user.id))
  }

  const onSwitchContentUserRole = ({ role, userId }: { role: ContentUserRole; userId: number }) => {
    dispatch(switchContentUserRole({ role, userId }))
  }

  const onMakePresenter = (userId: number) => {
    if (isCurrentUserRecorder()) {
      dispatch(openMakePresenterDialog(userId))
      return
    }
    dispatch(makePresenter(userId))
  }

  const onClickShowHealthInfo = () => {
    setIsShowHealthInfoPopupOpened(true)
  }

  const onCloseShowHealthInfo = () => {
    setIsShowHealthInfoPopupOpened(false)
  }

  let roles = useSelector((state) => {
    const availableRoles = [UserRole.User, UserRole.Viewer]

    const isSessionGuestRoleEnabled = getIsSessionGuestRoleEnabled(state)
    if (isSessionGuestRoleEnabled) {
      availableRoles.push(UserRole.SessionGuest)
    }

    return availableRoles.map((role) => ({
      role,
      displayName: getSessionRoleDisplayName(state, { Role: role }),
    }))
  })

  roles = roles.filter(({ role }) => role !== user.role)

  return (
    <>
      <Popover
        anchorEl={anchorEl}
        open={isOpen}
        onClose={onClose}
        onClick={onClose}
        PaperProps={{
          sx: {
            minWidth: 150,
          },
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
      >
        {isCertificatesMenuVisible && (
          <UserOptionButton onClick={onCertificatesDialogOpen} buttonText='Show Certificates' />
        )}
        {isContentUserQualificationManagementVisible && (
          <UserOptionButton
            onClick={onContentUserQualificationsDialogOpen}
            buttonText='Manage required qualifications'
          />
        )}
        {isShowHealthDataVisible && <UserOptionButton onClick={onClickShowHealthInfo} buttonText='Show Health Info' />}
        {isMakePresenterVisible && (
          <UserOptionButton onClick={() => onMakePresenter(user.id)} buttonText='Make Presenter' />
        )}
        {isSwitchRoleButtonsVisible &&
          roles.map(({ role, displayName }) => (
            <UserOptionButton
              key={role}
              onClick={() => onSwitchContentUserRole({ role, userId: user.id })}
              buttonText={`Make ${displayName}`}
            />
          ))}
        {isKickButtonVisible && <UserOptionButton onClick={onKickUserRequested} buttonText='Remove User' />}
      </Popover>

      <ConfirmationDialog
        onCloseDialog={onKickUserAborted}
        onSubmit={onKickUserSubmitted}
        isOpen={isKickUserDialogOpened}
        title='Remove user?'
        submitButtonText='Yes'
        cancelButtonText='No'
      />
      <HealthInfoDialog isOpen={isShowHealthInfoPopupOpened} onClose={onCloseShowHealthInfo} userId={user.id} />
      <UserCertificatesDialog
        open={certificatesDialogOpen}
        user={{ Id: user.id, IdentityGuid: user.identityGuid }}
        onClose={onCertificatesDialogClose}
      />
      <EditContentUserQualificationsDialog
        userId={user.id}
        userIdentityGuid={user.identityGuid}
        userName={user.displayName}
        contentId={contentId}
        qualificationIds={user.requiredQualificationIds}
        open={contentUserQualificationsDialogOpen}
        onClose={onContentUserQualificationsDialogClose}
      />
    </>
  )
}

export default memo(UserOptionsMenu)
